import { scaleTime, scaleLinear } from 'd3-scale'
import dayjs, { Dayjs, OpUnitType } from 'dayjs'
import {
  red,
  pink,
  purple,
  deepPurple,
  indigo,
  blue,
  lightBlue,
  cyan,
  teal,
  green,
  lightGreen,
  lime,
  yellow,
  amber,
  orange,
  deepOrange,
  brown,
  blueGrey,
} from '@mui/material/colors'
import { GroupOption } from '@/types/metrics'

const colors = [
  red,
  deepPurple,
  lightBlue,
  green,
  yellow,
  deepOrange,
  blueGrey,
  pink,
  indigo,
  cyan,
  lightGreen,
  amber,
  brown,
  purple,
  blue,
  teal,
  lime,
  orange,
]

export const getColor = (index: number, colorIndex = 200) => {
  if (index > colors.length) {
    let colorShadeOffset = Math.floor((index + 1) / colors.length)
    // Condition to keep the shade range between 200 and 900
    if (colorShadeOffset > 7) {
      colorShadeOffset = colorShadeOffset % 7 ? (colorShadeOffset % 7) - 1 : 7
    }
    colorIndex += colorShadeOffset * 100
  }

  const mainColor: Record<number, string> = colors[index % colors.length]
  return mainColor[colorIndex]
}

const getFormat = (
  groupedBy: Omit<GroupOption, 'week'>,
  includeYear: boolean,
) => {
  switch (groupedBy) {
    case 'hour':
      return `MMM DD h${includeYear ? ' A, YY' : ':mm A'}`
    case 'day':
      return `MMM DD${includeYear ? ', YY' : ''}`
    case 'month':
      return includeYear ? 'MMM, YY' : 'MMMM'
    case 'year':
      return 'YYYY'
  }
}

const getRangeFormat = (
  start: Dayjs,
  unitType: OpUnitType,
  includeYear: boolean,
  to: Dayjs,
) => {
  let end = start.endOf(unitType)

  if (to.isBefore(end, 'day')) {
    end = to
  }

  const startStr = start.format(getFormat('day', false))
  const endStr = end.format(getFormat('day', includeYear))
  return `${startStr} - ${endStr}`
}

export const formatter = (
  value: string | Date,
  groupedBy: GroupOption,
  includeYear: boolean,
  to: Date, // used for range formatting
  useRanges?: boolean,
) => {
  const start = dayjs(value)
  const djsTo = dayjs(to)

  switch (groupedBy) {
    case 'hour':
    case 'day':
      return start.format(getFormat(groupedBy, includeYear))
    case 'week':
      return getRangeFormat(start, 'week', includeYear, djsTo)
    case 'month':
    case 'year': {
      if (
        !useRanges ||
        (start.date() == 1 &&
          start.endOf(groupedBy).isSameOrBefore(djsTo, 'day'))
      ) {
        return start.format(getFormat(groupedBy, includeYear))
      }
      return getRangeFormat(start, groupedBy, includeYear, djsTo)
    }
  }
}

export const getTimeTicks = (
  from: Date,
  to: Date,
  groupedBy: GroupOption,
  includeYear: boolean,
  ticksQuantity?: number, // d3 uses this number as a reference, but it might return more or fewer values depending on the domain
) => {
  const scale = scaleTime().domain([from, to]).nice()
  const ticks = Array.from(new Set(scale.ticks(ticksQuantity ?? 60)))
  return ticks.map((tick) => formatter(tick, groupedBy, includeYear, to))
}

export const getNumberTicks = (
  from: number,
  to: number,
  ticksQuantity?: number, // d3 uses this number as a reference, but it might return more or fewer values depending on the domain
  onlyIntegers?: boolean,
) => {
  const scale = scaleLinear().domain([from, to]).nice()
  const ticks = Array.from(
    new Set(
      scale
        .ticks(ticksQuantity ?? 60)
        .map((tick) => (onlyIntegers ? Math.round(tick) : tick)),
    ),
  )
  return ticks
}

export const getChartHeight = (dataLength: number, minimumHeight: number) => {
  return Math.max(minimumHeight, dataLength * Math.max(30 - dataLength, 20))
}

export const getLeftMarginForCountChart = (yValue: number | string) => {
  const digits = yValue.toString().replace(/[\s.]/g, '').length
  return Math.max(30, digits * 9)
}

export const getBottomMarginForCountChart = (
  needsToRotate: boolean,
  includeYear: boolean,
  showingRange: boolean,
  groupingByHour?: boolean,
) => {
  let bottomMargin = 30
  if (needsToRotate) {
    if (includeYear || groupingByHour) {
      if (showingRange || groupingByHour) {
        bottomMargin = 115
      } else {
        bottomMargin = 90
      }
    } else {
      bottomMargin = showingRange ? 100 : 50
    }
  }
  return bottomMargin
}
